import type { ChatStats } from '#/services/stats/service.js'
import { bucketWindow, STATS_BUCKETS, STATS_MONTH, STATS_RECENT } from '#/services/stats/spec.js'
import { clear, el } from './dom.js'
import { statsIcon } from './icons.js'
import { t } from './labels.js'
import { renderSectionError, sectionHead } from './sheet.js'

// The read-only stats section at the bottom of the dashboard — the Mini App face of /stats. Split like
// form.ts: planStats() is the PURE shaping of the service's ChatStats over the SHARED bucket spec
// (services/stats/spec.ts — the same table + remainder rule the in-chat summary renders, so the two
// surfaces can't disagree) — unit-tested; the render*() functions are the thin DOM transport (live-verify
// only). Deliberately NOT a chart: 8–9 heterogeneous classes read as a labeled table (house grouped-table
// rows), and comparing bar lengths across unlike actions (a ban vs a settings edit) would be a false
// comparison. The row glyphs stay bot-only (the web dropped emoji prefixes for the SVG-icon look).

export interface StatsRow { labelKey: string, recent: number, month: number }
export interface StatsModel { rows: StatsRow[], other: { recent: number, month: number } | null, totals: { recent: number, month: number } }

/**
 * PURE: shape a ChatStats into display rows. rows[i] aligns with STATS_BUCKETS[i]; `other` (the audited
 * actions no bucket claims) is non-null only when it has activity — same visibility rule as the bot's
 * summary. Returns null when either shared window is missing from the response — that is a malformed
 * reply, and the caller renders the loud error state rather than a half-picture.
 */
export function planStats(stats: ChatStats): StatsModel | null {
  const recent = stats.windows.find(w => w.key === STATS_RECENT.key)
  const month = stats.windows.find(w => w.key === STATS_MONTH.key)
  if (!recent || !month)
    return null
  const r = bucketWindow(recent)
  const m = bucketWindow(month)
  const rows = STATS_BUCKETS.map((b, i) => ({ labelKey: b.labelKey, recent: r.buckets[i]!, month: m.buckets[i]! }))
  const other = r.other > 0 || m.other > 0 ? { recent: r.other, month: m.other } : null
  // The big number on top of the tray (s104) is the SUM of what the rows below show (+ `other`, which
  // bucketWindow derives as total − named): the headline can never disagree with the rows it sits on.
  const sum = (pick: (x: { recent: number, month: number }) => number): number =>
    rows.reduce((acc, row) => acc + pick(row), 0) + (other ? pick(other) : 0)
  return { rows, other, totals: { recent: sum(x => x.recent), month: sum(x => x.month) } }
}

// One "label …… 12 (30)" row in the house grouped-table style: the recent count first, the month count
// muted in parentheses — the exact format the in-chat /stats prints, so an admin who knows one surface
// can read the other. Numbers are integers + static labels → safe text nodes throughout (XSS-safe).
// Exported: the owner console's detail sheet renders the SAME pairing (one format, one place).
export function statsRowEl(label: string, recent: number, month: number): HTMLElement {
  return el('div', { class: 'row' }, el('span', { class: 'label' }, label), el('span', { class: 'value' }, `${recent} (${month})`))
}

// The section head: the column-chart icon + title + the "?" info sheet — the shared sectionHead (sheet.ts),
// the same skeleton the log-channel section uses, so the two can't drift on the DOM or a11y contract.
function statsHead(): HTMLElement {
  return sectionHead(statsIcon, 'ui-stats-title', 'ui-stats-body')
}

/** Render the loaded stats into `root` (replacing its content — re-runs on language switch). */
export function renderStats(root: HTMLElement, stats: ChatStats): void {
  const model = planStats(stats)
  if (!model) {
    renderStatsError(root) // a reply missing a shared window — fail loud, never render a half-picture
    return
  }
  clear(root)
  root.append(statsHead())
  // The caption decodes the "12 (30)" number pair once for the whole section; its 7/30 wording is pinned
  // to the shared STATS_WINDOWS by labels.gen.test.ts, so it can't silently drift from the real windows.
  root.append(el('div', { class: 'stats-caption' }, t('ui-stats-caption')))
  const group = el('div', { class: 'sec-group' })
  // The headline (s104, the «consumi» panel of Alessio's style example): the 7-day count, large, with the
  // 30-day one beside it; the per-kind rows follow.
  group.append(el(
    'div',
    { class: 'stats-hero' },
    el('div', { class: 'stats-now' }, el('span', { class: 'stats-cap' }, t('ui-stats-recent')), el('span', { class: 'stats-num' }, String(model.totals.recent))),
    el('div', { class: 'stats-tot' }, el('span', { class: 'stats-cap' }, t('ui-stats-month')), el('span', { class: 'stats-val' }, String(model.totals.month))),
  ))
  for (const row of model.rows)
    group.append(statsRowEl(t(row.labelKey), row.recent, row.month))
  if (model.other)
    group.append(statsRowEl(t('stats-row-other'), model.other.recent, model.other.month))
  root.append(group)
}

/** Render the loud in-section failure state — scoped here so a stats hiccup never blocks the form. */
export function renderStatsError(root: HTMLElement): void {
  renderSectionError(root, statsHead(), 'ui-stats-error')
}
